<template>
  <div v-if="colorScale && max - min > step" class="slider">
    <MinMaxSliderFilter
      :label="title"
      labelType="label"
      :from="fromValue"
      :to="toValue"
      :min="min"
      :max="max"
      :step="step"
      :fromColor="fromColor"
      :toColor="toColor"
      :middleColor="middleColor"
      :format="format"
      @updateValue="updateValue"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import useColoredEntities from '@/composables/useColoredEntities'
import { ColorScaleConfig, controlledColorScale } from '@/utils/colorScale'
import MinMaxSliderFilter from './MinMaxSliderFilter.vue'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'

type Props = {
  fromColor: string
  toColor: string
  middleColor: string
  title: string
  min?: number
  max?: number
  step?: number
  format?: FormatOptions
}

const props = defineProps<Props>()

const coloredEntities = useColoredEntities()

const key = ref(coloredEntities.currentKey)
const fromValue = ref(0)
const toValue = ref(0)

const toClosestUp = (value: number) => Math.ceil(value / step.value) * step.value
const toClosestDown = (value: number) => Math.floor(value / step.value) * step.value

const minRaw = computed(
  () =>
    props.min ??
    coloredEntities.data?.reduce((acc, curr) => Math.min(acc, curr.value), Infinity) ??
    0
)
const maxRaw = computed(
  () =>
    props.max ??
    coloredEntities.data?.reduce((acc, curr) => Math.max(acc, curr.value), -Infinity) ??
    0
)

const step = computed(
  () => props.step ?? Math.pow(10, Math.floor(Math.log10(maxRaw.value - minRaw.value))) / 100
)

const min = computed(() => toClosestDown(minRaw.value))
const max = computed(() =>
  toClosestUp(
    coloredEntities.data?.reduce((acc, curr) => Math.max(acc, curr.value), -Infinity) ?? 0
  )
)

const colorScale = computed<ColorScaleConfig | undefined>(() =>
  controlledColorScale(
    'STEP',
    [min.value, fromValue.value, toValue.value, max.value],
    [props.fromColor, props.middleColor, props.toColor]
  )
)

watch(
  colorScale,
  (colorScale) => {
    if (colorScale && coloredEntities.currentKey === key.value)
      coloredEntities.setColorScale(colorScale)
  },
  { immediate: true }
)

const percentile = (data: any[], p: number) => {
  const sorted = [...data].sort((a, b) => a.value - b.value)
  const pos = (sorted.length - 1) * p
  const base = Math.floor(pos)
  const rest = pos - base

  if (sorted[base + 1] !== undefined) {
    return sorted[base].value + rest * (sorted[base + 1].value - sorted[base].value)
  } else {
    return sorted[base].value
  }
}

watch(
  () => coloredEntities.data,
  (data) => {
    if (data && data.length > 0) {
      // Use 25th and 75th percentiles
      fromValue.value = toClosestDown(percentile(data, 0.25))
      toValue.value = toClosestUp(percentile(data, 0.75))
    }
  },
  { immediate: true }
)

onMounted(() => {
  scrollToBottom()
})

function scrollToBottom() {
  const rangeSlider = document.querySelector('.range-slider')
  if (rangeSlider) {
    rangeSlider.scrollTop = rangeSlider.scrollHeight // Scroll to the bottom of the container
  }
}

function updateValue(value: { from: number; to: number }) {
  fromValue.value = value.from
  toValue.value = value.to
}
</script>

<style lang="scss" scoped>
.slider {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  padding: 24px;
  width: 350px;
}
</style>
